import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CmsserviceService } from '../../common-cms-service/cmsservice.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';


export interface WeekData {
  weekNumber: number;
  subjects: string[];
}

@Component({
    selector: 'app-eleven-topic-test',
    templateUrl: './eleven-topic-test.component.html',
    styleUrls: ['./eleven-topic-test.component.css'],
    standalone: false
})
export class ElevenTopicTestComponent implements OnInit {

 title = '11+ Topic Tests – Weekly Preparation | Sehyog';

  // ── Filter model ───────────────────────────────────────────
  filters: any = {
    pattern: null,
    mode: null,
    center: null,
    search: ''
  };

  // ── Filter option lists ────────────────────────────────────
  patternTypes: any[] = [];
  centers: string[] = [];

  // ── Data ───────────────────────────────────────────────────
  testList: any[] = [];
  private allResults: any[] = [];

  // ── State ──────────────────────────────────────────────────
  ngUnsubscribe = new Subject<void>();
  openFaq: number = -1;

  // ── Static content ─────────────────────────────────────────
  testimonials: any[] = [
    {
      name: 'Alumni Parent, Barnet',
      text: 'These weekly tests helped my child gradually master every topic. By the time the full mocks started, she was confident and ready.'
    },
    {
      name: 'Alumni Parent, Chelmsford',
      text: 'The Topic Tests are a game-changer. They highlight weak areas and give students a structured routine that actually works.'
    },
    {
      name: 'Alumni Parent, Milton Keynes',
      text: 'We saw consistent improvement week after week. The systematic approach made all the difference in our child\'s preparation.'
    }
  ];

  faqs: any[] = [
    {
      q: 'What age group are these tests for?',
      a: 'Designed for students preparing for 11+ exams — typically Year 5 pupils (aged 9–10). Tests align to major exam boards including GL Assessment, CEM and CSSE.'
    },
    {
      q: 'Are the tests online or offline?',
      a: 'Both options are available. Complete tests online through our platform or attend face-to-face sessions — depending on your child\'s preference and learning style.'
    },
    {
      q: 'How long does each test take?',
      a: 'Approximately 30–45 minutes per weekly topic test. Designed to fit comfortably into a busy school week without causing fatigue or overwhelm.'
    },
    {
      q: 'Which exam formats do you cover?',
      a: 'We cover CSSE, CEM, GL Assessment, PACE and FSCE boards. Tests are conducted across Harrow, Barnet, Chelmsford, Brentwood, Amersham, Milton Keynes and Bexley regions.'
    },
    {
      q: 'Do I receive feedback after each test?',
      a: 'Yes — every test includes a detailed Result Analysis Report (RAR) highlighting strengths, weaknesses and recommended focus areas for the following week.'
    }
  ];

  // ── Constructor ────────────────────────────────────────────
  constructor(
    private router: ActivatedRoute,
    private titleService: Title,
    private categoryProduct: CmsserviceService,
    private route: Router,
    private ref: ChangeDetectorRef
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────
  ngOnInit(): void {
    this.titleService.setTitle(this.title);
    this.loadFiltersAndData();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // ── Load filter options then all topic test pages ──────────
  private loadFiltersAndData(): void {
    this.categoryProduct.getTestFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: any) => {
        const data = (res && res.data) ? res.data : {};
        const allTypes = data.type ? data.type : [];

        const allowedPatternNames = [
          'GL', 'CEM', 'CSSE', 'FSCE', 'SET',
          'GL Redbridge', 'GL Kent', 'GLQE Barnet', 'GL Bucks', 'GL Bexley', 'GL HBS'
        ];
        this.patternTypes = allTypes.filter((t: any) =>
          allowedPatternNames.indexOf(t.type) !== -1
        );

        this.fetchAllPages();
      }, (err: any) => {
        console.error('getTestFilters error', err);
        this.fetchAllPages();
      });
  }

  // ── Fetch all pages recursively, filter client-side ────────
  // Mirrors exactly how ElevPlusTestsComponent loads all results,
  // then isTopicTest() keeps only category 19 / topic-named items.
  private fetchAllPages(): void {
    this.allResults = [];
    const maxPages = 50;
    let pagesFetched = 0;

    const finalise = () => {
      this.centers = this.buildCenters(this.allResults);
      this.applyFilters();
      this.ref.detectChanges();
    };

    const fetchPage = (pageNum: number) => {
      if (pageNum > maxPages) { finalise(); return; }
      pagesFetched++;

      const userId = +localStorage.getItem('userid') || 0;

      // Send the same broad payload as the mock test page —
      // NO category pre-filter so the API returns everything,
      // then we filter client-side with isTopicTest().
      const formData: any = {
        release: 'new',
        exam_type: 'both',
        search: '',
        course: [],
        category: [],   // empty = fetch all categories
        type: [],
        user_id: userId,
        page: pageNum
      };

      this.categoryProduct.getTestList(formData)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((res: any) => {
          const chunk: any[] = (res && res.data) ? res.data : [];
          if (chunk.length === 0) { finalise(); return; }

          // Keep only topic tests
          const topicChunk = chunk.filter((t: any) => this.isTopicTest(t));
          this.allResults = this.allResults.concat(topicChunk);

          const extra = (res && res.extra && typeof res.extra.extraCount === 'number')
            ? res.extra.extraCount : null;

          if (extra !== null) {
            if (extra > 0) { fetchPage(pageNum + 1); } else { finalise(); }
          } else {
            fetchPage(pageNum + 1);
          }
        }, (err: any) => {
          console.error('Error fetching topic tests page', err);
          finalise();
        });
    };

    fetchPage(1);
  }

  // ── Is topic test ──────────────────────────────────────────
  // Category 19 is the primary topic test ID.
  // Also catch by name/category string in case the API returns
  // different category_ids for the same content.
  private isTopicTest(t: any): boolean {
    if (!t) return false;
    const cid      = Number(t.category_id);
    const name     = (t.test_name  || '').toString().toLowerCase();
    const cat      = (t.category   || '').toString().toLowerCase();
    const course   = (t.course     || '').toString().toLowerCase();
    const courseId = Number(t.course_id);

    // Must be 11+ content first
    const ELEVENPLUS_CATEGORY_IDS = [16, 19, 39, 28, 29, 30, 31, 32, 33];
    const is11Plus = (
      course === '11plus' || courseId === 1 ||
      ELEVENPLUS_CATEGORY_IDS.indexOf(cid) !== -1 ||
      name.indexOf('11+') !== -1 || name.indexOf('11plus') !== -1
    );
    if (!is11Plus) return false;

    // Then narrow to topic tests
    const TOPIC_IDS = [19];
    return (
      TOPIC_IDS.indexOf(cid) !== -1 ||
      name.indexOf('topic') !== -1  ||
      cat.indexOf('topic')  !== -1
    );
  }

  // ── Filter methods ─────────────────────────────────────────
  applyFilters(): void {
    let filtered = this.allResults.slice();

    // Board
    if (this.filters.pattern != null) {
      const meta  = this.patternTypes.find((p: any) => String(p.type_id) === String(this.filters.pattern));
      const label = meta ? (meta.type || '').toLowerCase() : '';
      if (label) {
        filtered = filtered.filter((t: any) =>
          ((t.type || '') + '').toLowerCase().indexOf(label) !== -1 ||
          ((t.test_name || '') + '').toLowerCase().indexOf(label) !== -1
        );
      }
    }

    // Mode
    if (this.filters.mode && this.filters.mode !== 'Both') {
      const modeVal = (this.filters.mode || '').toLowerCase();
      filtered = filtered.filter((t: any) =>
        ((t.exam_type || '') + '').toLowerCase().indexOf(modeVal) !== -1
      );
    }

    // Centre
    if (this.filters.center) {
      const cn = (this.filters.center || '').toLowerCase();
      filtered = filtered.filter((t: any) =>
        ((t.all_location || t.location || '') + '').toLowerCase().indexOf(cn) !== -1
      );
    }

    // Search
    if (this.filters.search) {
      const q = (this.filters.search + '').toLowerCase().trim();
      if (q) {
        filtered = filtered.filter((t: any) =>
          ((t.test_name || '') + '').toLowerCase().indexOf(q) !== -1 ||
          ((t.all_location || t.location || '') + '').toLowerCase().indexOf(q) !== -1 ||
          ((t.category || '') + '').toLowerCase().indexOf(q) !== -1 ||
          ((t.type || '') + '').toLowerCase().indexOf(q) !== -1
        );
      }
    }

    this.testList = this.sortUpcomingFirst(filtered);
    this.ref.detectChanges();
  }

  setMode(mode: string | null): void {
    this.filters.mode = mode;
    this.applyFilters();
  }

  resetFilters(): void {
    this.filters = { pattern: null, mode: null, center: null, search: '' };
    this.applyFilters();
  }

  loadMore(): void {
    this.applyFilters();
  }

  // ── Centre builder ─────────────────────────────────────────
  private buildCenters(list: any[]): string[] {
    const set: any = {};
    for (const item of list) {
      const v = ((item.all_location || item.location || '') + '').trim();
      if (v) set[v] = true;
    }
    return Object.keys(set).sort();
  }

  // ── Date parsing ───────────────────────────────────────────
  private monthMap: any = { JAN:0,FEB:1,MAR:2,APR:3,MAY:4,JUN:5,JUL:6,AUG:7,SEP:8,OCT:9,NOV:10,DEC:11 };
  private fullMonthMap: any = { JANUARY:0,FEBRUARY:1,MARCH:2,APRIL:3,MAY:4,JUNE:5,JULY:6,AUGUST:7,SEPTEMBER:8,OCTOBER:9,NOVEMBER:10,DECEMBER:11 };

  private parseDateString(s: string): number | null {
    if (!s) return null;
    const str = s.trim();
    let m: any;

    m = str.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (m) {
      let y = parseInt(m[3], 10);
      if (y < 100) y += 2000;
      return Date.UTC(y, parseInt(m[2], 10) - 1, parseInt(m[1], 10));
    }
    m = str.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) return Date.UTC(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));

    m = str.match(/(?:from\s+)?(?:mon|tue|wed|thu|fri|sat|sun)?\s*([A-Za-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s*(\d{4}))?/i);
    if (m) {
      const monName = m[1].toUpperCase();
      const dd = parseInt(m[2], 10);
      const yy = m[3] ? parseInt(m[3], 10) : new Date().getFullYear();
      let idx = this.fullMonthMap[monName];
      if (idx == null) idx = this.monthMap[monName.substring(0, 3)];
      if (idx != null) return Date.UTC(yy, idx, dd);
    }
    m = str.match(/(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})/);
    if (m) {
      const mn = m[2].toUpperCase();
      let mi = this.fullMonthMap[mn];
      if (mi == null) mi = this.monthMap[mn.substring(0, 3)];
      if (mi != null) return Date.UTC(parseInt(m[3], 10), mi, parseInt(m[1], 10));
    }
    return null;
  }

  private getTestTimestamp(t: any): number {
    const td = this.parseDateString((t && t.test_date) || '');
    if (td != null) return td;
    const tn = this.parseDateString((t && t.test_name) || '');
    if (tn != null) return tn;
    return Number.NaN;
  }

  private sortUpcomingFirst(list: any[]): any[] {
    const now = Date.now();
    const future: any[] = [], past: any[] = [], unknown: any[] = [];
    for (const item of list) {
      const ts = this.getTestTimestamp(item);
      if (isNaN(ts))      { unknown.push(item); }
      else if (ts >= now) { future.push({ ts, item }); }
      else                { past.push({ ts, item }); }
    }
    future.sort((a, b) => a.ts - b.ts);
    past.sort((a, b) => b.ts - a.ts);
    return [...future.map(x => x.item), ...past.map(x => x.item), ...unknown];
  }

  // ── Template helpers ───────────────────────────────────────
  trimChar(str: string, length: number = 40): string {
    if (!str) return '';
    return str.length < length ? str : str.substring(0, length) + '...';
  }

  hasDate(dateStr: string): boolean {
    return !!dateStr && this.parseDateString(dateStr) != null;
  }

  private getDateObj(dateStr: string): Date | null {
    if (!dateStr) return null;
    const ts = this.parseDateString(dateStr);
    return ts != null ? new Date(ts) : null;
  }

  getDateDay(dateStr: string): string {
    const d = this.getDateObj(dateStr);
    return d ? String(d.getUTCDate()) : '';
  }

  getDateMon(dateStr: string): string {
    const d = this.getDateObj(dateStr);
    if (!d) return '';
    return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()];
  }

  getDateYr(dateStr: string): string {
    const d = this.getDateObj(dateStr);
    return d ? String(d.getUTCFullYear()) : '';
  }

  isRangeDate(dateStr: string): boolean {
    return !!dateStr && /^(from\s+)/i.test(dateStr.trim());
  }

  toggleFaq(i: number): void {
    this.openFaq = this.openFaq === i ? -1 : i;
  }

  // ── Navigation helpers ─────────────────────────────────────
  scrollToCalendar(): void {
    const el = document.getElementById('calendar-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }

  downloadCalendar(): void {
    window.location.href = 'https://drive.google.com/file/d/1o3M4Trb2pfrSltPOHr6-Q3GnzRNULIbK/view?usp=sharing';
  }

}